import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Calendar } from 'src/registry/ui/Calendar';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { PopoverPortal } from '@radix-ui/react-popover';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { generalMessages } from 'src/utils/messages';
import { SubscriptionItemUsageRecordFormSchemaValues } from 'src/features/subscriptionItemUsageRecords/utils/formSchema';
import { subItemUsageRecordMessages } from 'src/features/subscriptionItemUsageRecords/utils/messages';
import * as React from 'react';

type SubscriptionItemUsageRecordFormProps = {
    form: UseFormReturn<SubscriptionItemUsageRecordFormSchemaValues>;
    onSubmit: (values: SubscriptionItemUsageRecordFormSchemaValues) => void;
};

type DateFieldName = 'billing_period_start' | 'billing_period_end';

function SubscriptionItemUsageRecordForm({ form, onSubmit }: SubscriptionItemUsageRecordFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const [openCalendar, setOpenCalendar] = React.useState<DateFieldName | null>(null);

    const renderDateField = (name: DateFieldName, label: string, required: boolean) => (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog" required={required}>
                        {label}
                    </FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <Popover
                                open={openCalendar === name}
                                onOpenChange={(open) => setOpenCalendar(open ? name : null)}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start text-base font-normal"
                                        onClick={() => setOpenCalendar(name)}
                                    >
                                        <FontAwesomeIcon
                                            icon={ICON_MAP.CALENDAR}
                                            className="text-primary-700 mr-1 size-4"
                                        />
                                        <span className={cn(!field.value && 'text-gray-500')}>
                                            {field.value
                                                ? formatDate(new Date(field.value))
                                                : `${formatMessage(generalMessages.selectDate)}...`}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverPortal>
                                    <PopoverContent
                                        className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                        align="end"
                                    >
                                        <Calendar
                                            captionLayout="dropdown"
                                            mode="single"
                                            selected={field.value ? new Date(field.value) : undefined}
                                            onSelect={(date) => {
                                                if (date) {
                                                    field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                    setOpenCalendar(null);
                                                }
                                            }}
                                        />
                                    </PopoverContent>
                                </PopoverPortal>
                            </Popover>
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="recorded_at"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(subItemUsageRecordMessages.recordedAt)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    captionLayout="dropdown"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <InputFormField<SubscriptionItemUsageRecordFormSchemaValues>
                form={form}
                name="quantity"
                type="number"
                label={formatMessage(subItemUsageRecordMessages.quantity)}
            />
            {renderDateField(
                'billing_period_start',
                formatMessage(subItemUsageRecordMessages.billingPeriodStart),
                true,
            )}
            {renderDateField('billing_period_end', formatMessage(subItemUsageRecordMessages.billingPeriodEnd), false)}
            <p className="text-sm text-gray-500">{formatMessage(subItemUsageRecordMessages.billingPeriodHint)}</p>
        </Form>
    );
}

export default SubscriptionItemUsageRecordForm;
